import React, { createContext, useContext, useReducer, Dispatch, ReactNode } from 'react';

export const DRAWING_MODES = {
  NOTE: 'NOTE',
  FIELD: 'FIELD',
  MARKER: 'MARKER',
  SELECT: 'SELECT',
  FIELD_HOLE: 'FIELD_HOLE',
  EDIT_FIELD_BOUNDARIES: 'EDIT_FIELD_BOUNDARIES',
  SPLIT_FIELD: 'SPLIT_FIELD',
  SPLIT_FIELD_POLYGONS: 'SPLIT_FIELD_POLYGONS',
  EDIT_MERGED_FIELD: 'EDIT_MERGED_FIELD',
};

export type DrawingMode = (typeof DRAWING_MODES)[keyof typeof DRAWING_MODES];

export interface SplitFieldInputs {
  fieldAName: string;
  fieldANumber: string;
  fieldBName: string;
  fieldBNumber: string;
}

export interface MergedFieldInputs {
  fieldName: string;
  fieldNumber: string;
}

export interface PolygonDrawingState {
  markers: any[];
  polygons: any[];
  actions: any[];
  acIndex: number;
  selectedMarker: any | null;
  drawingMode: DrawingMode;
  polygonVisible: boolean;
  size: number;
  changingThreshold: number;
  showEditFieldBoundariesConfirmation: boolean;
  showSplitFieldConfirmation: boolean;
  showEditMergedFieldConfirmation: boolean;
  showSplitFieldInputDialog: boolean;
  splitFieldInputs: SplitFieldInputs;
  showMergeFieldInputDialog: boolean;
  mergedFieldInputs: MergedFieldInputs;
  resetDone: boolean;
  measuring: boolean;
  measurementMarkers: any[];
  selectedMeasurementLine: any | null;
  playAnimation: boolean;
}

export type PolygonDrawingAction =
  | { type: 'SET_DRAWING_MODE'; payload: DrawingMode }
  | { type: 'SET_MARKERS'; payload: any[] }
  | { type: 'SET_POLYGONS'; payload: { polygons: any[]; polygonVisible: boolean } }
  | { type: 'SET_POLYGON_VISIBLE'; payload: boolean }
  | { type: 'ADD_MARKER'; payload: { markers: any[]; actions: any[]; acIndex: number } }
  | {
      type: 'FINISH_POLYGON';
      payload: { polygons: any[]; polygonVisible: boolean; actions: any[]; acIndex: number };
    }
  | {
      type: 'DELETE_SELECTED_MARKER';
      payload: { polygons: any[]; actions: any[]; acIndex: number };
    }
  | {
      type: 'INSERT_MARKER';
      payload: { polygons: any[]; actions: any[]; acIndex: number; selectedMarker: any };
    }
  | {
      type: 'MOVE_MARKER';
      payload: { polygons: any[]; actions: any[]; acIndex: number; selectedMarker: any };
    }
  | { type: 'SET_SHOW_DIALOG'; payload: { key: string; value: boolean } }
  | { type: 'SET_SPLIT_FIELD_INPUTS'; payload: { key: keyof SplitFieldInputs; value: string } }
  | { type: 'SET_MERGE_FIELD_INPUTS'; payload: { key: keyof MergedFieldInputs; value: string } }
  | { type: 'SET_SELECTED_MARKER'; payload: any | null }
  | { type: 'SET_CHANGING_THRESHOLD'; payload: number }
  | {
      type: 'INITIALIZE_EDIT_FIELD_BOUNDARIES' | 'INITIALIZE_EDIT_MERGED_FIELD';
      payload: { actions: any[]; acIndex: number; polygons: any[] };
    }
  | { type: 'SET_MEASURING'; payload: boolean }
  | { type: 'SET_MEASUREMENT_MARKERS'; payload: any[] }
  | {
      type:
        | 'ADD_MEASUREMENT_MARKER'
        | 'MOVE_MEASUREMENT_MARKER'
        | 'DELETE_SELECTED_MEASUREMENT_LINE';
      payload: { measurementMarkers: any[]; actions: any[]; acIndex: number };
    }
  | { type: 'SET_SELECTED_MEASUREMENT_LINE'; payload: any | null }
  | { type: 'SET_PLAY_ANIMATION'; payload: boolean }
  | { type: 'MAGIC_SELECTION'; payload: { polygons: any[]; actions: any[]; acIndex: number } }
  | {
      type: 'UNDO' | 'REDO' | 'SET_SPLIT_FIELD_POLYGONS';
      payload: { polygons: any[]; actions: any[]; acIndex: number };
    }
  | { type: 'RESET' };

const getInitialState = (): PolygonDrawingState => ({
  markers: [],
  polygons: [],
  actions: [],
  acIndex: -1,
  selectedMarker: null,
  drawingMode: 'NOTE',
  polygonVisible: false,
  size: 0,
  changingThreshold: 7,
  showEditFieldBoundariesConfirmation: false,
  showSplitFieldConfirmation: false,
  showEditMergedFieldConfirmation: false,
  showSplitFieldInputDialog: false,
  splitFieldInputs: {
    fieldAName: '',
    fieldANumber: '',
    fieldBName: '',
    fieldBNumber: '',
  },
  showMergeFieldInputDialog: false,
  mergedFieldInputs: {
    fieldName: '',
    fieldNumber: '',
  },
  resetDone: false,
  measuring: false,
  measurementMarkers: [],
  selectedMeasurementLine: null,
  playAnimation: false,
});

function reducer(state: PolygonDrawingState, action: PolygonDrawingAction): PolygonDrawingState {
  switch (action.type) {
    case 'SET_DRAWING_MODE':
      return {
        ...state,
        drawingMode: action.payload,
      };
    case 'SET_MARKERS':
      return {
        ...state,
        markers: action.payload,
      };
    case 'SET_POLYGONS':
      return {
        ...state,
        polygons: action.payload.polygons,
        polygonVisible: action.payload.polygonVisible,
      };
    case 'SET_POLYGON_VISIBLE':
      return {
        ...state,
        polygonVisible: action.payload,
      };
    case 'ADD_MARKER':
      return {
        ...state,
        markers: action.payload.markers,
        actions: action.payload.actions,
        acIndex: action.payload.acIndex,
      };
    case 'FINISH_POLYGON':
      return {
        ...state,
        polygons: action.payload.polygons,
        polygonVisible: action.payload.polygonVisible,
        actions: action.payload.actions,
        acIndex: action.payload.acIndex,
      };
    case 'SET_SELECTED_MARKER':
      return {
        ...state,
        selectedMarker: action.payload,
        selectedMeasurementLine: null,
      };
    case 'DELETE_SELECTED_MARKER':
      return {
        ...state,
        polygons: action.payload.polygons,
        actions: action.payload.actions,
        acIndex: action.payload.acIndex,
        selectedMarker: null,
      };
    case 'INSERT_MARKER':
    case 'MOVE_MARKER':
      return {
        ...state,
        polygons: action.payload.polygons,
        actions: action.payload.actions,
        acIndex: action.payload.acIndex,
        selectedMarker: action.payload.selectedMarker,
      };
    case 'SET_SHOW_DIALOG':
      return {
        ...state,
        [action.payload.key]: action.payload.value,
      };
    case 'SET_SPLIT_FIELD_INPUTS':
      return {
        ...state,
        splitFieldInputs: {
          ...state.splitFieldInputs,
          [action.payload.key]: action.payload.value,
        },
      };
    case 'SET_MERGE_FIELD_INPUTS':
      return {
        ...state,
        mergedFieldInputs: {
          ...state.mergedFieldInputs,
          [action.payload.key]: action.payload.value,
        },
      };
    case 'SET_CHANGING_THRESHOLD':
      return {
        ...state,
        changingThreshold: action.payload,
      };
    case 'INITIALIZE_EDIT_MERGED_FIELD':
    case 'INITIALIZE_EDIT_FIELD_BOUNDARIES':
      return {
        ...state,
        actions: action.payload.actions,
        acIndex: action.payload.acIndex,
        polygons: action.payload.polygons,
        polygonVisible: true,
        markers: [],
      };
    case 'SET_SPLIT_FIELD_POLYGONS':
      return {
        ...state,
        polygons: action.payload.polygons,
        actions: action.payload.actions,
        acIndex: action.payload.acIndex,
        markers: [],
        measurementMarkers: [],
        polygonVisible: true,
      };
    case 'UNDO':
    case 'REDO':
      return {
        ...state,
        polygons: action.payload.polygons,
        actions: action.payload.actions,
        acIndex: action.payload.acIndex,
        selectedMarker: null,
      };
    case 'SET_MEASURING':
      return {
        ...state,
        measuring: action.payload,
        selectedMarker: null,
        selectedMeasurementLine: null,
      };
    case 'SET_MEASUREMENT_MARKERS':
      return {
        ...state,
        measurementMarkers: action.payload,
      };
    case 'ADD_MEASUREMENT_MARKER':
    case 'MOVE_MEASUREMENT_MARKER':
    case 'DELETE_SELECTED_MEASUREMENT_LINE':
      return {
        ...state,
        measurementMarkers: action.payload.measurementMarkers,
        actions: action.payload.actions,
        acIndex: action.payload.acIndex,
        selectedMeasurementLine: null,
      };
    case 'SET_SELECTED_MEASUREMENT_LINE':
      return {
        ...state,
        selectedMeasurementLine: action.payload,
        selectedMarker: null,
      };
    case 'SET_PLAY_ANIMATION':
      return {
        ...state,
        playAnimation: action.payload,
      };
    case 'MAGIC_SELECTION':
      return {
        ...state,
        polygons: action.payload.polygons,
        polygonVisible: true,
        actions: action.payload.actions,
        acIndex: action.payload.acIndex,
      };
    case 'RESET':
      return { ...getInitialState(), resetDone: true };
    default:
      return state;
  }
}

interface PolygonDrawingContextType {
  state: PolygonDrawingState;
  dispatch: Dispatch<PolygonDrawingAction>;
}

export const PolygonDrawingContext = createContext<PolygonDrawingContextType | undefined>(
  undefined
);

interface ProviderProps {
  children: ReactNode;
}

export function PolygonDrawingProvider({ children }: ProviderProps) {
  const [state, dispatch] = useReducer(reducer, getInitialState());

  return (
    <PolygonDrawingContext.Provider value={{ state, dispatch }}>
      {children}
    </PolygonDrawingContext.Provider>
  );
}

export function usePolygonDrawing() {
  const context = useContext(PolygonDrawingContext);
  if (!context) {
    throw new Error('usePolygonDrawing must be used within a PolygonDrawingProvider');
  }
  return context;
}
